import React, { useRef, useState } from "react"
import {
  Box, Flex, Text, Textarea,
} from "@chakra-ui/react"
import { Control, useController } from "react-hook-form"
import Icon from "components/elements/Icon"
import Popover from "components/dialogs/Popover"
import Menu from "components/dialogs/Menu"
import { MergeFieldMenuGroup, MergeFieldMenuItem } from "components/MergeFieldMenu"
import { WhatsAppTemplateVariable } from "sharedTypes"
import { EDITABLE_FIELD_CHARACTER_LIMIT } from "utilities/whatsappTemplates"
import AttachmentButton from "./AttachmentButton"

export type FormGroupOption = {
  label: string
  value: string
  disabled: boolean
}

export type FormGroup = {
  label: string
  options: FormGroupOption[]
}

type PillSize = "sm" | "md" | "lg"

type VariableUIKind = "locked" | "editable" | "nameField" | "formLink" | "media"

export const getVariableUIKind = (variable: WhatsAppTemplateVariable): VariableUIKind => {
  if (variable.type === "locked") return "locked"
  if (variable.type === "media") return "media"
  if (variable.type === "user") {
    if (variable.source === "name_fields") return "nameField"
    if (variable.source === "forms") return "formLink"

    return "editable"
  }
  // Legacy type support (during transition before YAML update)
  if (variable.type === "editable") return "editable"
  if (variable.type === "selectable") return "formLink"

  return "locked"
}

const NAME_FIELD_OPTIONS = [
  { value: "{{FIRST_NAME}}", label: "First Name" },
  { value: "{{FULL_NAME}}", label: "Full Name" },
  { value: "{{LAST_NAME}}", label: "Last Name" },
]

type Props = {
  variable: WhatsAppTemplateVariable
  name: string
  control?: Control<any>
  readOnly?: boolean
  forms?: FormGroup[]
  size?: PillSize
  onSave?: () => void
  eventId?: string
  textMessageId?: string
}

export const PILL_SIZES = {
  sm: {
    fontSize: "xs" as const, px: 1, iconSize: 2.5, gap: 0.5,
  },
  md: {
    fontSize: "md" as const, px: 1.5, iconSize: 3, gap: 1,
  },
  lg: {
    fontSize: "xl" as const, px: 2, iconSize: 3.5, gap: 1.5,
  },
}

export const PILL_STYLES = {
  locked: {
    bg: "gray.100", color: "gray.600", borderColor: "gray.300", icon: "lock",
  },
  editable: {
    bg: "orange.50", color: "orange.700", borderColor: "orange.200", icon: "pencil",
  },
  selectable: {
    bg: "blue.50", color: "blue.700", borderColor: "blue.200", icon: "down-arrow",
  },
  media: {
    bg: "gray.100", color: "gray.600", borderColor: "gray.300", icon: "attachment",
  },
  error: {
    bg: "red.50", color: "red.700", borderColor: "red.200", icon: "",
  },
}

export type PillVariant = keyof typeof PILL_STYLES

const UI_KIND_TO_PILL_VARIANT: Record<VariableUIKind, PillVariant> = {
  locked: "locked",
  editable: "editable",
  nameField: "selectable",
  formLink: "selectable",
  media: "media",
}

export const PillShell = ({
  variant, isEmpty, children, onClick, cursor, size = "sm", icon: iconOverride, ariaLabel,
  showTrailingIcon = true,
}: {
  variant: PillVariant
  isEmpty?: boolean
  children: React.ReactNode
  onClick?: () => void
  cursor?: string
  size?: PillSize
  icon?: string
  ariaLabel?: string
  showTrailingIcon?: boolean
}) => {
  const baseStyle = PILL_STYLES[variant]
  const sizeStyle = PILL_SIZES[size]
  const colorStyle = isEmpty ? PILL_STYLES.error : baseStyle

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      bg={colorStyle.bg}
      color={colorStyle.color}
      borderWidth={1}
      borderColor={colorStyle.borderColor}
      borderRadius="md"
      px={sizeStyle.px}
      py="1px"
      fontSize={sizeStyle.fontSize}
      fontWeight="medium"
      gap={sizeStyle.gap}
      cursor={cursor || "default"}
      onClick={onClick}
      verticalAlign="baseline"
      mx={0.5}
      lineHeight="normal"
      userSelect="none"
      aria-label={ariaLabel}
    >
      {children}
      {showTrailingIcon && (
        <Icon icon={iconOverride || baseStyle.icon} size={sizeStyle.iconSize} />
      )}
    </Box>
  )
}

const LockedPill = ({ variable, size = "sm" }: { variable: WhatsAppTemplateVariable, size?: PillSize }) => (
  <PillShell variant="locked" size={size} cursor="not-allowed">
    <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{variable.label}</Text>
  </PillShell>
)

type EditablePillProps = {
  variable: WhatsAppTemplateVariable
  name: string
  control: Control<any>
  size?: PillSize
  onSave?: () => void
}

const EditablePill = ({
  variable, name, control, size = "sm", onSave,
}: EditablePillProps) => {
  const { field } = useController({ name, control, defaultValue: "" })
  const [localValue, setLocalValue] = useState(field.value ?? "")
  const [isOpen, setIsOpen] = useState(false)
  const cancelledRef = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const isEmpty = !field.value?.trim()
  const displayLabel = isEmpty ? variable.label : field.value

  const handleSave = () => {
    setIsOpen(false)
    if (cancelledRef.current) {
      cancelledRef.current = false

      return
    }
    if (localValue !== field.value) {
      field.onChange(localValue)
      onSave?.()
    }
  }

  const handleCancel = () => {
    cancelledRef.current = true
    setLocalValue(field.value ?? "")
    setIsOpen(false)
  }

  return (
    <Popover
      placement="bottom"
      header={variable.label}
      initialFocusRef={textareaRef}
      isOpen={isOpen}
      onOpen={() => {
        setLocalValue(field.value ?? "")
        setIsOpen(true)
        requestAnimationFrame(() => {
          const el = textareaRef.current
          if (el) el.setSelectionRange(el.value.length, el.value.length)
        })
      }}
      onClose={handleSave}
      body={(
        <Box p={3}>
          <Textarea
            ref={textareaRef}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSave()
              }
              if (e.key === "Escape") {
                e.preventDefault()
                handleCancel()
              }
            }}
            placeholder="Enter text..."
            maxLength={EDITABLE_FIELD_CHARACTER_LIMIT}
            rows={2}
            resize="none"
          />
          <Flex justifyContent="space-between" mt={2}>
            <Text fontSize="sm" color="gray.500">
              Character limit
            </Text>
            <Text
              fontSize="sm"
              color={localValue.length <= EDITABLE_FIELD_CHARACTER_LIMIT
                ? "gray.500" : "red.500"}
            >
              {EDITABLE_FIELD_CHARACTER_LIMIT - localValue.length}
            </Text>
          </Flex>
        </Box>
      )}
      trigger={(
        <PillShell
          variant="editable"
          isEmpty={isEmpty}
          cursor="pointer"
          size={size}
          ariaLabel={isEmpty ? `Edit ${variable.label}, empty` : undefined}
        >
          <Text
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            maxW="250px"
          >
            {displayLabel}
          </Text>
        </PillShell>
      )}
    />
  )
}

type SelectablePillProps = {
  variable: WhatsAppTemplateVariable
  name: string
  control: Control<any>
  forms: FormGroup[]
  size?: PillSize
  onSave?: () => void
}

const SelectablePill = ({
  variable, name, control, forms, size = "sm", onSave,
}: SelectablePillProps) => {
  const { field } = useController({ name, control, defaultValue: "" })

  const selectedForm = forms
    .flatMap((group) => group.options)
    .find((option) => `{{FORM_${option.value}}}` === field.value)

  const isEmpty = !field.value
  const displayLabel = selectedForm ? `Form Link: ${selectedForm.label}` : variable.label

  const menuItems = forms.flatMap((group) => [
    <MergeFieldMenuGroup key={group.label} title={group.label}>
      {group.options.map((option) => (
        <MergeFieldMenuItem
          key={option.value}
          onClick={() => {
            field.onChange(`{{FORM_${option.value}}}`)
            onSave?.()
          }}
          selected={`{{FORM_${option.value}}}` === field.value}
          isDisabled={option.disabled}
        >
          {option.label}
        </MergeFieldMenuItem>
      ))}
    </MergeFieldMenuGroup>,
  ])

  return (
    <Menu
      placement="bottom"
      noPadding
      button={(
        <PillShell
          variant="selectable"
          isEmpty={isEmpty}
          cursor="pointer"
          size={size}
          ariaLabel={isEmpty ? `Pick ${variable.label}, empty` : undefined}
        >
          <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="250px">{displayLabel}</Text>
        </PillShell>
      )}
      menuItems={menuItems}
      maxH="50vh"
    />
  )
}

type NameFieldPillProps = {
  variable: WhatsAppTemplateVariable
  name: string
  control: Control<any>
  size?: PillSize
  onSave?: () => void
}

const NameFieldPill = ({
  variable, name, control, size = "sm", onSave,
}: NameFieldPillProps) => {
  const { field } = useController({ name, control, defaultValue: "" })

  const selectedOption = NAME_FIELD_OPTIONS.find((opt) => opt.value === field.value)
  const isEmpty = !field.value
  const displayLabel = selectedOption?.label ?? variable.label

  const menuItems = NAME_FIELD_OPTIONS.map((option) => (
    <MergeFieldMenuItem
      key={option.value}
      onClick={() => {
        field.onChange(option.value)
        onSave?.()
      }}
      selected={option.value === field.value}
    >
      {option.label}
    </MergeFieldMenuItem>
  ))

  return (
    <Menu
      placement="bottom"
      noPadding
      button={(
        <PillShell
          variant="selectable"
          isEmpty={isEmpty}
          cursor="pointer"
          size={size}
          ariaLabel={isEmpty ? `Pick ${variable.label}, empty` : undefined}
        >
          <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" maxW="250px">{displayLabel}</Text>
        </PillShell>
      )}
      menuItems={menuItems}
      maxH="50vh"
    />
  )
}

const VariablePill = ({
  variable, name, control, readOnly, forms = [], size = "sm", onSave, eventId, textMessageId,
}: Props) => {
  const uiKind = getVariableUIKind(variable)
  const pillVariant = UI_KIND_TO_PILL_VARIANT[uiKind]

  if (readOnly || !control) {
    return (
      <PillShell variant={pillVariant} size={size}>
        <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{variable.label}</Text>
      </PillShell>
    )
  }

  switch (uiKind) {
    case "locked":
      return <LockedPill variable={variable} size={size} />
    case "editable":
      return (
        <EditablePill
          variable={variable}
          name={name}
          control={control}
          size={size}
          onSave={onSave}
        />
      )
    case "nameField":
      return (
        <NameFieldPill
          variable={variable}
          name={name}
          control={control}
          size={size}
          onSave={onSave}
        />
      )
    case "formLink":
      return (
        <SelectablePill
          variable={variable}
          name={name}
          control={control}
          forms={forms}
          size={size}
          onSave={onSave}
        />
      )
    case "media":
      return (
        <AttachmentButton
          name={name}
          control={control}
          mediaType={variable.mediaType}
          onSave={onSave}
          eventId={eventId}
          textMessageId={textMessageId}
        />
      )
    default:
      return <LockedPill variable={variable} size={size} />
  }
}

export default VariablePill
